import {
  Button,
  ListGroup,
  Loading,
  Modal,
  Overlay,
} from '@cubeyou-team/hx-react-components';
import { getProductScope } from '@discover-frontend/utils';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { requestRepository } from 'src/actions/RepositoryActions';
import { addToast } from 'src/actions/ToastActions';
import { MODAL_TYPES } from 'src/components/RepositoryItemActions/constants';
import { QUERY_TYPE_IDS } from '@discover-frontend/constants';
import translations from 'src/lang';
import { useWatch } from 'react-hook-form';
import { CAMPAIGN_STATUS } from '../../constants';
import { useAudienceActivationContext } from '../../providers/AudienceActivationProvider';
import { SelectedDestinationMap } from '../../types';
import { useFetchTargets } from './api/hooks/useFetchTargets';
import { usePostCampaign } from './api/hooks/usePostCampaign';
import { usePutCampaign } from './api/hooks/usePutCampaign';
import { TARGET_MESSAGE_CODE } from './api/request';
import { ActivationErrorContainer } from './components/ActivationErrorContainer';
import { AudienceActivationModalForm } from './components/AudienceActivationModalForm';
import { useAudienceForm } from './hooks/useAudienceForm';

const lang = 'en-US';
const translation = translations[lang].MODALS.AUDIENCE_ACTIVATION;

const StyledAudienceActivationModal = styled(Modal)`
  > :nth-child(2) {
    padding-bottom: 0;
    overflow-x: hidden;
    display: grid;
    grid-template-rows: auto 3.75rem;
  }
`;

export interface FormState {
  selectedDestinations: SelectedDestinationMap;
}

export const AudienceActivationModal = () => {
  const {
    runId,
    modalType,
    repositoryItem,
    analyseData,
    showActivationModal,
    selectedDestinations,
    closeModal,
    setModalType,
  } = useAudienceActivationContext();
  const dispatch = useDispatch();

  const { control, trigger, getValues, errors, isValid } = useAudienceForm();

  const {
    mutate: createCampaign,
    isLoading: isPostCampaignLoading,
  } = usePostCampaign();

  const {
    mutate: updateCampaign,
    isLoading: isPutCampaignLoading,
  } = usePutCampaign();

  const {
    data,
    isLoading: isFetchTargetsLoading,
    error: fetchTargetError,
  } = useFetchTargets(showActivationModal);

  const isError = useMemo(() => {
    if (fetchTargetError) return true;
    if (data.code !== TARGET_MESSAGE_CODE.SUCCESS) return true;

    return false;
  }, [fetchTargetError, data]);

  const [campaignName, startDate, endDate] = useWatch({
    control,
    name: ['campaignName', 'dateRange.startDate', 'dateRange.endDate'],
  });

  const saveDisabled = useMemo(() => {
    return (
      isError ||
      !isValid ||
      selectedDestinations.size === 0 ||
      campaignName.length === 0 ||
      startDate.length === 0 ||
      endDate.length === 0
    );
  }, [
    isError,
    isValid,
    selectedDestinations,
    campaignName,
    startDate,
    endDate,
  ]);

  const draftDisbaled = useMemo(
    () => isError || modalType === MODAL_TYPES.OPEN_CAMPAIGN,
    [isError, modalType]
  );

  const onSettledCreateOrUpdateCampaign = useCallback(() => {
    closeModal();
    dispatch(requestRepository(QUERY_TYPE_IDS.CAMPAIGN));
  }, [dispatch, closeModal]);

  const showToast = useCallback(
    (isDraft: boolean, isSuccess: boolean) => {
      let key = isSuccess ? 'SUCCESS' : 'ERROR';

      if (isDraft) key += '_DRAFT';

      dispatch(
        addToast({
          status: isSuccess ? 'success' : 'error',
          message: translations['en-US'].TOASTS.AUDIENCE_ACTIVATION[key],
        })
      );
    },
    [dispatch]
  );

  const activateCampaign = useCallback(
    (isDraft: boolean) => {
      if (modalType === MODAL_TYPES.OPEN_CAMPAIGN) {
        setModalType(MODAL_TYPES.DUPLICATE_CAMPAIGN);
        return;
      }

      const destinations = Array.from(selectedDestinations.values()).map(
        item => ({
          destination: item.destination,
          additional_properties: item.additional_properties,
        })
      );

      const {
        campaignName,
        additionalNotes,
        dateRange: { startDate, endDate },
      } = getValues();

      const body = {
        campaignName,
        startDate,
        endDate,
        additionalNotes,
        runId,
        scope: getProductScope(),
        nodeId: repositoryItem ? repositoryItem.id : analyseData?.query?.id,
        selectedDestinations: destinations,
        status: isDraft ? CAMPAIGN_STATUS.DRAFT : CAMPAIGN_STATUS.ACTIVE,
      };

      const mutationArgs = {
        onSuccess: response =>
          showToast(isDraft, response?.data?.code === 'SUCCESS'),
        onError: () => showToast(isDraft, false),
        onSettled: onSettledCreateOrUpdateCampaign,
      };

      if (
        modalType === MODAL_TYPES.NEW_CAMPAIGN ||
        modalType === MODAL_TYPES.DUPLICATE_CAMPAIGN
      ) {
        return createCampaign(body, {
          ...mutationArgs,
        });
      }

      updateCampaign(
        { campaignId: repositoryItem.campaignId, payload: body },
        {
          ...mutationArgs,
        }
      );
    },
    [
      runId,
      modalType,
      selectedDestinations,
      analyseData,
      repositoryItem,
      getValues,
      showToast,
      setModalType,
      createCampaign,
      updateCampaign,
      onSettledCreateOrUpdateCampaign,
    ]
  );

  const handleActivate = useCallback(() => activateCampaign(false), [
    activateCampaign,
  ]);

  const handleSaveDraft = useCallback(() => activateCampaign(true), [
    activateCampaign,
  ]);

  return (
    <Overlay isOpen={showActivationModal} position="right">
      <StyledAudienceActivationModal
        title={translation.MODAL_TITLE}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                type="submit"
                size="small"
                onClick={handleActivate}
                disabled={saveDisabled}
                loading={isPostCampaignLoading || isPutCampaignLoading}
              >
                {modalType === MODAL_TYPES.OPEN_CAMPAIGN
                  ? translation.BUTTON_DUPLICATE_CAMPAIGN
                  : translation.BUTTON_ACTIVATE}
              </Button>,
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={draftDisbaled}
                loading={isPostCampaignLoading || isPutCampaignLoading}
              >
                {translation.BUTTON_SAVE_DRAFT}
              </Button>,
            ]}
          />
        }
        closable
        handleClose={closeModal}
      >
        <Loading isLoading={isFetchTargetsLoading}>
          {isError ? (
            <ActivationErrorContainer errorCode={data.code} />
          ) : (
            <AudienceActivationModalForm
              control={control}
              errors={errors}
              trigger={trigger}
              getValues={getValues}
            />
          )}
        </Loading>
      </StyledAudienceActivationModal>
    </Overlay>
  );
};
